import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Tabs,
  Tab,
  Button,
  IconButton,
  Chip,
  useMediaQuery,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Plus,
  Trash2,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  Lock,
  CalendarDays,
  ListTree,
  Package,
  ChevronRight,
} from 'lucide-react';

const API_BASE = 'https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com';
const PLATFORM_NAMES = ['Shopify', 'Amazon', 'Walmart'];

const PLATFORM_COLORS = {
  Shopify: '#10b981',
  Amazon: '#f59e0b',
  Walmart: '#2383e2',
};

const MasterPOListPage = () => {
  const [masterPOs, setMasterPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    poId: null,
    targetPaid: false,
  });

  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');

  const statusTabs = ['All', 'Open', 'Order Placed', 'Backordered', 'Closed', 'Paid', 'Unpaid'];

  const handleTabChange = (e, newStatus) => setSelectedStatus(newStatus);

  const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

  const formatDate = (value) => {
    if (!value) return 'Unknown';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPlatformTotals = (po, platform) => {
    return (po.childDetails || []).reduce(
      (acc, child) => {
        if (!child || child.platform !== platform || !Array.isArray(child.products)) return acc;
        child.products.forEach((p) => {
          const qty = p.quantityOrdered || 0;
          const unitPrice = p.unitPrice || 0;
          const listPrice = p.listPrice ?? p.unitPrice ?? 0;
          acc.actual += qty * unitPrice;
          acc.list += qty * listPrice;
        });
        return acc;
      },
      { actual: 0, list: 0 }
    );
  };

  const getGrandTotals = (po) => {
    return (po.childDetails || []).reduce(
      (acc, child) => {
        if (!Array.isArray(child.products)) return acc;
        child.products.forEach((p) => {
          const qty = p.quantityOrdered || 0;
          const unitPrice = p.unitPrice || 0;
          acc.actual += qty * unitPrice;
        });
        return acc;
      },
      { actual: 0, list: 0 }
    );
  };

  const getTotalProducts = (po) => {
    return (po.childDetails || []).reduce((acc, child) => {
      return acc + (child.products?.length || 0);
    }, 0);
  };

  const filteredPOs = useMemo(() => {
    return masterPOs.filter((po) => {
      if (selectedStatus === 'All') return true;
      if (selectedStatus === 'Paid') return po.paid === true;
      if (selectedStatus === 'Unpaid') return po.paid === false;
      return po.status === selectedStatus;
    });
  }, [masterPOs, selectedStatus]);

  const countMap = useMemo(
    () => ({
      All: masterPOs.length,
      Open: masterPOs.filter((po) => po.status === 'Open').length,
      'Order Placed': masterPOs.filter((po) => po.status === 'Order Placed').length,
      Backordered: masterPOs.filter((po) => po.status === 'Backordered').length,
      Closed: masterPOs.filter((po) => po.status === 'Closed').length,
      Paid: masterPOs.filter((po) => po.paid === true).length,
      Unpaid: masterPOs.filter((po) => po.paid === false).length,
    }),
    [masterPOs]
  );


  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    const fetchPageData = async () => {
      setLoading(true);
      try {
        await axios.get(`${API_BASE}/api/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const response = await axios.get(`${API_BASE}/api/master-pos`);
        const masterPos = response.data || [];

        const enrichedPOs = await Promise.all(
          masterPos.map(async (po) => {
            if (!po.childPos || po.childPos.length === 0) return { ...po, childDetails: [] };
            const childDetails = await Promise.all(
              po.childPos.map(async (childId) => {
                try {
                  const res = await axios.get(`${API_BASE}/api/pos/${childId}`);
                  return res.data;
                } catch (err) {
                  console.error(`Failed to fetch child PO ${childId}`, err);
                  return null;
                }
              })
            );
            return { ...po, childDetails: childDetails.filter(Boolean) };
          })
        );

        setMasterPOs(enrichedPOs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) { navigate('/login'); return; }
        console.error('Error fetching Master POs:', err);
        toast.error('Failed to fetch Master POs');
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [navigate]);

  const openPaymentDialog = (poId, targetPaid) => setPaymentDialog({ open: true, poId, targetPaid });
  const closePaymentDialog = () => setPaymentDialog({ open: false, poId: null, targetPaid: false });

  const confirmPaymentChange = async () => {
    const { poId, targetPaid } = paymentDialog;
    const t = toast.loading(targetPaid ? 'Marking as paid…' : 'Marking as unpaid…');
    try {
      if (targetPaid) {
        await axios.put(`${API_BASE}/api/master-pos/${poId}/pay`);
      } else {
        await axios.put(`${API_BASE}/api/master-pos/${poId}/unpay`);
      }
      setMasterPOs((prev) => prev.map((po) => (po._id === poId ? { ...po, paid: targetPaid } : po)));
      toast.success(targetPaid ? 'Marked as Paid' : 'Marked as Unpaid', { id: t });
    } catch (err) {
      console.error('Failed to update payment status:', err);
      toast.error('Failed to update payment status', { id: t });
    } finally {
      closePaymentDialog();
    }
  };

  const handleCloseDeleteDialog = () => { setDeleteDialogOpen(false); setDeleteId(null); };

  const handleDelete = async (deleteChildren) => {
    if (!deleteId) return;
    setIsDeleting(true);
    const t = toast.loading('Deleting purchase order…');
    try {
      if (deleteChildren) {
        await axios.delete(`${API_BASE}/api/master-pos/${deleteId}?deleteChildren=true`);
      } else {
        await axios.put(`${API_BASE}/api/master-pos/${deleteId}/remove-all-children`);
        await axios.delete(`${API_BASE}/api/master-pos/${deleteId}`);
      }
      toast.success('Purchase order deleted', { id: t });
      setMasterPOs((prevPOs) => prevPOs.filter((po) => po._id !== deleteId));
    } catch (err) {
      console.error('Error deleting PO:', err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || 'Error deleting PO', { id: t });
    } finally {
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open': return <Clock size={12} />;
      case 'Order Placed': return <CheckCircle size={12} />;
      case 'Backordered': return <AlertTriangle size={12} />;
      case 'Closed': return <Lock size={12} />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return '#f59e0b';
      case 'Order Placed': return '#10b981';
      case 'Backordered': return '#ef4444';
      case 'Closed': return '#6b7280';
      default: return '#787774';
    }
  };

  return (
    <Box className="fade-in">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
            Purchase Orders
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Manage and track all purchase orders
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => navigate('/create-master-po')}
          fullWidth={isMobile}
          sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' }, px: 2.5 }}
        >
          New Purchase Order
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedStatus}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
        >
          {statusTabs.map((status) => (
            <Tab
              key={status}
              value={status}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <span>{status}</span>
                  <Box component="span" sx={{
                    fontSize: '0.6rem', fontWeight: 700,
                    bgcolor: selectedStatus === status ? 'primary.main' : 'action.hover',
                    color: selectedStatus === status ? '#fff' : 'text.secondary',
                    px: 0.75, py: 0.15, borderRadius: 1, minWidth: 18, textAlign: 'center',
                    transition: 'all 0.15s ease',
                  }}>
                    {countMap[status] || 0}
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Box
              key={`sk-${i}`}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
            >
              <Skeleton variant="rectangular" height={3} />
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1rem', mb: 0.5 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: 2 }} />
                      <Skeleton variant="rectangular" width={55} height={22} sx={{ borderRadius: 2 }} />
                    </Box>
                  </Box>
                  <Skeleton variant="text" width={80} sx={{ fontSize: '1.1rem' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                  <Skeleton variant="text" width={90} />
                  <Skeleton variant="text" width={60} />
                  <Skeleton variant="text" width={80} />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Skeleton variant="rectangular" width={100} height={26} sx={{ borderRadius: 1.5 }} />
                  <Skeleton variant="rectangular" width={100} height={26} sx={{ borderRadius: 1.5 }} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : filteredPOs.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Package size={32} style={{ color: '#b4b4b0', marginBottom: 12 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            No purchase orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No purchase orders match "{selectedStatus}".
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredPOs.map((po) => {
            const grandTotals = getGrandTotals(po);
            const productCount = getTotalProducts(po);
            const statusColor = getStatusColor(po.status);

            return (
              <Box
                key={po._id}
                onClick={() => navigate(`/master-po/${po._id}`)}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'primary.main',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  },
                }}
              >
                {/* Top accent bar */}
                <Box sx={{ height: 3, bgcolor: statusColor }} />

                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                  {/* Row 1: Name + Grand Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {po.poName || 'Unnamed PO'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          icon={getStatusIcon(po.status)}
                          label={po.status}
                          sx={{
                            height: 22, fontSize: '0.68rem', fontWeight: 600,
                            bgcolor: `${statusColor}12`, color: statusColor,
                            border: `1px solid ${statusColor}28`,
                            '& .MuiChip-icon': { color: statusColor, fontSize: 12 },
                          }}
                        />
                        <Chip
                          size="small"
                          label={po.paid ? 'Paid' : 'Unpaid'}
                          sx={{
                            height: 22, fontSize: '0.68rem', fontWeight: 600,
                            bgcolor: po.paid ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                            color: po.paid ? '#10b981' : '#ef4444',
                            border: `1px solid ${po.paid ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      {grandTotals.actual > 0 && (
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '1.1rem', lineHeight: 1.2 }}>
                          {formatCurrency(grandTotals.actual)}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                        <ChevronRight size={14} style={{ color: '#b4b4b0' }} />
                      </Box>
                    </Box>
                  </Box>

                  {/* Row 2: Meta info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 }, flexWrap: 'wrap', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarDays size={13} style={{ opacity: 0.5 }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                        {formatDate(po.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ListTree size={13} style={{ opacity: 0.5 }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                        {po.childPos ? po.childPos.length : 0} lists
                      </Typography>
                    </Box>
                    {productCount > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Package size={13} style={{ opacity: 0.5 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {productCount} products
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Row 3: Platform breakdown + Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {PLATFORM_NAMES.map((platform) => {
                        const totals = getPlatformTotals(po, platform);
                        if (totals.actual === 0) return null;
                        return (
                          <Box
                            key={platform}
                            sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              px: 1, py: 0.4, borderRadius: 1.5,
                              bgcolor: `${PLATFORM_COLORS[platform]}08`,
                              border: `1px solid ${PLATFORM_COLORS[platform]}18`,
                            }}
                          >
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: PLATFORM_COLORS[platform] }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.66rem', color: 'text.secondary' }}>
                              {platform}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.66rem' }}>
                              {formatCurrency(totals.actual)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} onClick={(e) => e.stopPropagation()}>
                      {po.status !== 'Open' && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => { e.stopPropagation(); openPaymentDialog(po._id, !po.paid); }}
                          sx={{ fontSize: '0.68rem', minWidth: 'auto', whiteSpace: 'nowrap', py: 0.25, px: 1 }}
                        >
                          {po.paid ? 'Mark Unpaid' : 'Mark Paid'}
                        </Button>
                      )}
                      {po.status === 'Open' && (
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setDeleteId(po._id); setDeleteDialogOpen(true); }}
                          sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'rgba(239,68,68,0.06)' } }}
                        >
                          <Trash2 size={15} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Purchase Order</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            Do you want to unassign the child Purchase Orders or delete them along with the Master PO?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={() => handleDelete(false)} color="warning" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={18} /> : 'Unassign Only'}
          </Button>
          <Button onClick={() => handleDelete(true)} color="error" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={18} /> : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onClose={closePaymentDialog}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {paymentDialog.targetPaid ? 'Mark as Paid' : 'Mark as Unpaid'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            Are you sure you want to {paymentDialog.targetPaid ? 'mark this PO as Paid' : 'mark this PO as Unpaid'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentDialog}>Cancel</Button>
          <Button onClick={confirmPaymentChange} color={paymentDialog.targetPaid ? 'success' : 'error'} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default MasterPOListPage;
